import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
} from '@dnd-kit/core';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { OrderCard } from '../components/OrderCard';
import { Modal } from '../components/Modal';
import { toast } from '../stores/toasts';
import type { Order, Status } from '../types';
import { KANBAN_STATUSES, getStatusIcon, getStatusLabel } from '../components/StatusBadge';
import { ORDER_STATUS_FLOW } from '@autoservice/shared';

interface ColumnProps {
  status: Status;
  orders: Order[];
}

function Column({ status, orders }: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div ref={setNodeRef} className={`kanban-col ${isOver ? 'drop-over' : ''}`}>
      <div className="kanban-col-header">
        <span>{getStatusIcon(status)} {getStatusLabel(status)}</span>
        <span className="kanban-col-count">{orders.length}</span>
      </div>
      <div className="kanban-col-body">
        {orders.map((o) => (
          <OrderCard key={o.id} order={o} />
        ))}
        {orders.length === 0 && (
          <div className="muted" style={{ fontSize: 12, textAlign: 'center', padding: 12 }}>
            Bo'sh
          </div>
        )}
      </div>
    </div>
  );
}

export function OrdersPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState<{ orderId: string; from: Status; to: Status } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data, isLoading } = useQuery({
    queryKey: ['orders', auth.activeWorkshop?.id, search],
    queryFn: () => api.listOrders({ pageSize: 100, q: search || undefined }),
    enabled: !!auth.activeWorkshop,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Status }) =>
      api.updateOrderStatus(id, status),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['orders', auth.activeWorkshop?.id] });
      const previous = qc.getQueryData(['orders', auth.activeWorkshop?.id, search]);
      qc.setQueryData(['orders', auth.activeWorkshop?.id, search], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((o: Order) => (o.id === id ? { ...o, status } : o)),
        };
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['orders', auth.activeWorkshop?.id, search], ctx.previous);
      toast.error('Status yangilanmadi');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['today-stats'] });
      toast.success('Status yangilandi');
    },
  });

  const grouped = useMemo(() => {
    const by: Record<Status, Order[]> = {
      CREATED: [],
      DIAGNOSTIC: [],
      REPAIRING: [],
      WAITING_PARTS: [],
      READY: [],
      DELIVERED: [],
      CANCELLED: [],
    };
    (data?.items ?? []).forEach((o) => {
      if (by[o.status]) by[o.status].push(o);
    });
    return by;
  }, [data]);

  function onDragEnd(e: DragEndEvent) {
    const orderId = String(e.active.id);
    const toStatus = String(e.over?.id ?? '') as Status;
    if (!toStatus) return;
    const order = data?.items.find((o) => o.id === orderId);
    if (!order) return;
    if (order.status === toStatus) return;
    const allowed = ORDER_STATUS_FLOW[order.status] ?? [];
    if (!allowed.includes(toStatus)) {
      toast.warning(`${getStatusLabel(order.status)} → ${getStatusLabel(toStatus)} mumkin emas`);
      return;
    }
    if (toStatus === 'DELIVERED' || toStatus === 'CANCELLED') {
      setConfirmModal({ orderId, from: order.status, to: toStatus });
      return;
    }
    updateStatus.mutate({ id: orderId, status: toStatus });
  }

  return (
    <>
      <div className="field">
        <input
          placeholder="🔍 Mashina, mijoz yoki telefon..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {isLoading ? (
        <div className="spinner" />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="kanban">
            {KANBAN_STATUSES.map((s) => (
              <Column key={s} status={s} orders={grouped[s] ?? []} />
            ))}
          </div>
        </DndContext>
      )}

      <Modal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title={confirmModal ? `Status: ${getStatusLabel(confirmModal.to)}` : ''}
        footer={
          confirmModal && (
            <>
              <button className="btn" onClick={() => setConfirmModal(null)}>Yo'q</button>
              <button
                className={`btn ${confirmModal.to === 'CANCELLED' ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => {
                  updateStatus.mutate({ id: confirmModal.orderId, status: confirmModal.to });
                  setConfirmModal(null);
                }}
                disabled={updateStatus.isPending}
              >
                Ha, tasdiqlash
              </button>
            </>
          )
        }
      >
        {confirmModal && (
          <div>
            <p>
              Buyurtmani <b>{getStatusLabel(confirmModal.to)}</b> ga o'tkazmoqchimisiz?
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              Bu amalni qaytarib bo'lmaydi (faqat admin orqali).
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
